import type { ApolloClient } from "@apollo/client";
import {
  COMMITTEE_ASSIGNMENTS_PAGE_SIZE,
  COMMITTEE_PARTICIPANT_EMAIL_MAX_PAGES,
  GROUP_EMAIL_RECIPIENT_FETCH_CONCURRENCY
} from "../constants";
import { BACKOFFICE_COMMITTEE_PARTICIPANT_EMAILS_QUERY } from "../queries";
import {
  DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY,
  type BackofficeCommitteeParticipantEmailsQueryResponse,
  type BackofficeCommitteeParticipantEmailsQueryVariables,
  type CommitteeParticipantEmailNode,
  type CommitteeParticipantEmailSource
} from "../types";

export const MAILTO_MAX_LENGTH = 2000;

export type MailtoField = "to" | "cc" | "bcc";
export type { CommitteeParticipantEmailNode };

export type CommitteeParticipantEmailResult = {
  emails: string[];
  participantCount: number;
  excludedCount: number;
  truncated: boolean;
  nodes: CommitteeParticipantEmailNode[];
};

export type GroupEmailParticipantEmailsResult = CommitteeParticipantEmailResult & {
  byCommittee: Record<string, CommitteeParticipantEmailResult>;
  failedCommitteeIds: string[];
};

async function mapSettledInPool<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = [];
  for (let index = 0; index < items.length; index += concurrency) {
    const chunk = items.slice(index, index + concurrency);
    results.push(...(await Promise.allSettled(chunk.map((item) => mapper(item)))));
  }
  return results;
}

export function isUnusableEmail(email: string | null | undefined): boolean {
  const trimmed = email?.trim() ?? "";
  if (!trimmed) return true;
  return trimmed.toLowerCase().endsWith(".invalid");
}

export function collectUsableEmails(emails: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const raw of emails) {
    if (isUnusableEmail(raw)) continue;
    const trimmed = raw?.trim() ?? "";
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

export function formatEmailAddressList(emails: string[]): string {
  return emails.join(",");
}

export function buildCommitteeMailtoHref({
  emails,
  field,
  subject
}: {
  emails: string[];
  field: MailtoField;
  subject: string;
}): string {
  const encodedSubject = encodeURIComponent(subject);
  const list = formatEmailAddressList(emails);
  const encodedList = emails.map((email) => encodeURIComponent(email)).join(",");

  if (field === "to") {
    return `mailto:${list}?subject=${encodedSubject}`;
  }

  if (field === "cc") {
    return `mailto:?cc=${encodedList}&subject=${encodedSubject}`;
  }

  return `mailto:?bcc=${encodedList}&subject=${encodedSubject}`;
}

export function isMailtoTooLong(href: string): boolean {
  return href.length > MAILTO_MAX_LENGTH;
}

export function openMailtoHref(href: string): void {
  window.location.assign(href);
}

export function groupEmailMailtoSubject(committeeNames: readonly string[]): string {
  if (committeeNames.length === 1) {
    return committeeNames[0] ?? "Committees";
  }
  return "Committees";
}

export function assignmentRoleFilter({
  catalogTitleIds,
  selectedTitleIds
}: {
  catalogTitleIds: readonly string[];
  selectedTitleIds: ReadonlySet<string>;
}): { titleIds?: readonly string[] } {
  if (catalogTitleIds.length === 0) return {};
  if (selectedTitleIds.size === 0) return { titleIds: [] };
  const allSelected = catalogTitleIds.every((id) => selectedTitleIds.has(id));
  if (allSelected) return {};
  return { titleIds: Array.from(selectedTitleIds) };
}

function resolveAssignmentEmail(
  node: CommitteeParticipantEmailNode,
  emailSource: CommitteeParticipantEmailSource
): string | null | undefined {
  if (emailSource === "person") {
    return node.person?.email ?? node.participantEmail;
  }
  return node.participantEmail;
}

export function collectEmailsFromAssignmentNodes(
  nodes: Array<CommitteeParticipantEmailNode | null | undefined>,
  {
    titleIds,
    emailSource = "participant"
  }: { titleIds?: readonly string[]; emailSource?: CommitteeParticipantEmailSource } = {}
): CommitteeParticipantEmailResult {
  const titleIdSet = titleIds === undefined ? null : new Set(titleIds);
  const seenPeople = new Set<string>();
  const rawEmails: string[] = [];
  const collectedNodes: CommitteeParticipantEmailNode[] = [];
  let participantCount = 0;
  let excludedCount = 0;

  for (const node of nodes) {
    if (!node) continue;
    if (titleIdSet !== null && (!node.title?.id || !titleIdSet.has(node.title.id))) continue;

    const person = node.person;
    if (!person?.id) continue;
    collectedNodes.push(node);
    if (seenPeople.has(person.id)) continue;
    seenPeople.add(person.id);
    participantCount += 1;

    const email = resolveAssignmentEmail(node, emailSource);
    if (isUnusableEmail(email)) {
      excludedCount += 1;
      continue;
    }

    rawEmails.push(email?.trim() ?? "");
  }

  return {
    emails: collectUsableEmails(rawEmails),
    participantCount,
    excludedCount,
    truncated: false,
    nodes: collectedNodes
  };
}

export async function fetchCommitteeParticipantEmails(
  client: ApolloClient<object>,
  {
    committeeId,
    includeInactive,
    emailSource = "participant"
  }: { committeeId: string; includeInactive: boolean; emailSource?: CommitteeParticipantEmailSource }
): Promise<CommitteeParticipantEmailResult> {
  const nodes: CommitteeParticipantEmailNode[] = [];
  let after: string | undefined;
  let hasNextPage = true;
  let truncated = false;
  let pages = 0;

  while (hasNextPage) {
    if (pages >= COMMITTEE_PARTICIPANT_EMAIL_MAX_PAGES) {
      truncated = true;
      break;
    }

    const variables: BackofficeCommitteeParticipantEmailsQueryVariables = {
      id: committeeId,
      first: COMMITTEE_ASSIGNMENTS_PAGE_SIZE,
      after,
      orderBy: DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY
    };
    if (!includeInactive) {
      variables.status = "ACTIVE";
    }

    const { data } = await client.query<
      BackofficeCommitteeParticipantEmailsQueryResponse,
      BackofficeCommitteeParticipantEmailsQueryVariables
    >({
      query: BACKOFFICE_COMMITTEE_PARTICIPANT_EMAILS_QUERY,
      variables,
      fetchPolicy: "no-cache"
    });

    const connection = data.backofficeCommittee?.assignments;
    nodes.push(...(connection?.nodes ?? []));
    pages += 1;

    hasNextPage = Boolean(connection?.pageInfo.hasNextPage);
    const nextAfter = connection?.pageInfo.endCursor ?? undefined;
    if (hasNextPage && (!nextAfter || nextAfter === after)) {
      truncated = true;
      break;
    }
    after = nextAfter;
  }

  return { ...collectEmailsFromAssignmentNodes(nodes, { emailSource }), truncated, nodes };
}

export async function fetchGroupEmailParticipantEmails(
  client: ApolloClient<object>,
  { committeeIds, titleIds }: { committeeIds: readonly string[]; titleIds?: readonly string[] }
): Promise<GroupEmailParticipantEmailsResult> {
  const uniqueCommitteeIds = Array.from(new Set(committeeIds.filter(Boolean)));
  const settled = await mapSettledInPool(uniqueCommitteeIds, GROUP_EMAIL_RECIPIENT_FETCH_CONCURRENCY, (committeeId) =>
    fetchCommitteeParticipantEmails(client, { committeeId, includeInactive: false })
  );

  const byCommittee: Record<string, CommitteeParticipantEmailResult> = {};
  const failedCommitteeIds: string[] = [];
  uniqueCommitteeIds.forEach((committeeId, index) => {
    const result = settled[index];
    if (result?.status === "fulfilled") {
      byCommittee[committeeId] = result.value;
      return;
    }
    failedCommitteeIds.push(committeeId);
  });

  if (uniqueCommitteeIds.length > 0 && Object.keys(byCommittee).length === 0) {
    const firstRejected = settled.find((result) => result.status === "rejected");
    throw firstRejected && firstRejected.status === "rejected"
      ? firstRejected.reason
      : new Error("Email addresses not loaded");
  }

  const mergedNodes = Object.values(byCommittee).flatMap((result) => result.nodes);
  const truncated = failedCommitteeIds.length > 0 || Object.values(byCommittee).some((result) => result.truncated);
  return {
    ...collectEmailsFromAssignmentNodes(mergedNodes, { titleIds }),
    truncated,
    nodes: mergedNodes,
    byCommittee,
    failedCommitteeIds
  };
}
